import {
  PAGINATION,
  STOCK_MOVEMENT_TYPES,
  formatDateTime,
  type StockBalance,
} from '@fdt/shared';
import AddIcon from '@mui/icons-material/Add';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorKey } from '../api/client';
import { ExportButton } from '../components/ExportButton';
import { FormDialog } from '../components/FormDialog';
import { PageHeader } from '../components/PageHeader';
import { useFormError } from '../hooks/useFormError';
import { currentLocale } from '../i18n';
import { productsApi, vehiclesApi, warehousesApi } from '../resources';
import { stockApi, type StockLocationRef } from '../resources/stock';

/**
 * Selects need every row at once. The API caps a page at
 * `PAGINATION.maxPageSize` and rejects anything larger with a 422, so this must
 * come from the shared constant rather than a guess.
 */
const ALL_PAGE_SIZE = PAGINATION.maxPageSize;

interface TransferLine {
  productId: string;
  quantity: string;
}

/** `warehouse:<id>` / `vehicle:<id>`, so one select can offer both. */
function parseLocationKey(key: string): StockLocationRef | null {
  const [kind, id] = key.split(':');
  if ((kind !== 'warehouse' && kind !== 'vehicle') || !id) {
    return null;
  }
  return { kind, id };
}

export function StockPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const queryClient = useQueryClient();

  const warehouses = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => warehousesApi.list({ page: 1, pageSize: ALL_PAGE_SIZE, isActive: 'true' }),
  });
  const vehicles = useQuery({
    queryKey: ['vehicles', 'all'],
    queryFn: () => vehiclesApi.list({ page: 1, pageSize: ALL_PAGE_SIZE, isActive: 'true' }),
  });
  const products = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () =>
      productsApi.list({ page: 1, pageSize: ALL_PAGE_SIZE, isActive: 'true', sortBy: 'name' }),
  });

  const locationOptions = useMemo(
    () => [
      ...(warehouses.data?.items ?? []).map((w) => ({
        key: `warehouse:${w.id}`,
        label: `${t('fields.warehouse')}: ${w.name}`,
      })),
      ...(vehicles.data?.items ?? []).map((v) => ({
        key: `vehicle:${v.id}`,
        label: `${t('fields.vehicle')}: ${v.plate}${v.label ? ` — ${v.label}` : ''}`,
      })),
    ],
    [warehouses.data, vehicles.data, t],
  );

  const [locationKey, setLocationKey] = useState('');
  // Default to the first warehouse once the lists arrive.
  const effectiveKey =
    locationKey || (warehouses.data?.items[0] ? `warehouse:${warehouses.data.items[0].id}` : '');
  const location = parseLocationKey(effectiveKey);

  const balance = useQuery({
    queryKey: ['stock', 'balance', effectiveKey],
    queryFn: () => stockApi.balance(location!),
    enabled: Boolean(location),
  });

  const [movementPage, setMovementPage] = useState(1);
  const [movementPageSize, setMovementPageSize] = useState(10);
  const movements = useQuery({
    queryKey: ['stock', 'movements', effectiveKey, movementPage, movementPageSize],
    queryFn: () =>
      stockApi.movements({
        page: movementPage,
        pageSize: movementPageSize,
        ...(location?.kind === 'warehouse' ? { warehouseId: location.id } : {}),
        ...(location?.kind === 'vehicle' ? { vehicleId: location.id } : {}),
      }),
    enabled: Boolean(location),
  });

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['stock'] });
  }

  // --- transfer dialog ---
  const [transferOpen, setTransferOpen] = useState(false);
  const [fromKey, setFromKey] = useState('');
  const [toKey, setToKey] = useState('');
  const [lines, setLines] = useState<TransferLine[]>([{ productId: '', quantity: '' }]);
  const [transferSaving, setTransferSaving] = useState(false);
  const transferError = useFormError();

  function openTransfer(): void {
    setFromKey(effectiveKey);
    setToKey('');
    setLines([{ productId: '', quantity: '' }]);
    transferError.clear();
    setTransferOpen(true);
  }

  async function submitTransfer(): Promise<void> {
    transferError.clear();
    const from = parseLocationKey(fromKey);
    const to = parseLocationKey(toKey);
    const usable = lines.filter((line) => line.productId && line.quantity.trim() !== '');

    if (!from || !to || usable.length === 0) {
      transferError.capture({ response: { data: { messageKey: 'stock.errors.incomplete' } } });
      return;
    }

    setTransferSaving(true);
    try {
      await stockApi.transfer({
        fromWarehouseId: from.kind === 'warehouse' ? from.id : null,
        fromVehicleId: from.kind === 'vehicle' ? from.id : null,
        toWarehouseId: to.kind === 'warehouse' ? to.id : null,
        toVehicleId: to.kind === 'vehicle' ? to.id : null,
        lines: usable,
      });
      setTransferOpen(false);
      refresh();
    } catch (error) {
      transferError.capture(error);
    } finally {
      setTransferSaving(false);
    }
  }

  // --- adjustment dialog ---
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustProductId, setAdjustProductId] = useState('');
  const [adjustQuantity, setAdjustQuantity] = useState('');
  const [adjustType, setAdjustType] = useState<'adjustment' | 'write_off' | 'purchase_in'>(
    'purchase_in',
  );
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);
  const adjustError = useFormError();

  function openAdjust(): void {
    setAdjustProductId('');
    setAdjustQuantity('');
    setAdjustType('purchase_in');
    setAdjustReason('');
    adjustError.clear();
    setAdjustOpen(true);
  }

  async function submitAdjust(): Promise<void> {
    adjustError.clear();
    if (!location || !adjustProductId || !adjustQuantity.trim() || !adjustReason.trim()) {
      adjustError.capture({ response: { data: { messageKey: 'stock.errors.incomplete' } } });
      return;
    }

    setAdjustSaving(true);
    try {
      await stockApi.adjust({
        warehouseId: location.kind === 'warehouse' ? location.id : null,
        vehicleId: location.kind === 'vehicle' ? location.id : null,
        productId: adjustProductId,
        // A write-off removes stock, so the sign is implied by the type rather
        // than left to whoever is typing.
        quantity:
          adjustType === 'write_off'
            ? `-${adjustQuantity.replace('-', '')}`
            : adjustQuantity,
        type: adjustType,
        reason: adjustReason,
      });
      setAdjustOpen(false);
      refresh();
    } catch (error) {
      adjustError.capture(error);
    } finally {
      setAdjustSaving(false);
    }
  }

  const totalUnits = (balance.data ?? []).reduce(
    (sum, row) => sum + Number.parseFloat(row.quantity),
    0,
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <PageHeader icon={StackedBarChartIcon} tone="info" title={t('nav.stock')} />
        <Stack direction="row" spacing={1}>
          <ExportButton
            file="stock.xlsx"
            params={
              location?.kind === 'warehouse'
                ? { warehouseId: location.id }
                : { vehicleId: location?.id }
            }
          />
          <Button variant="outlined" startIcon={<TuneIcon />} onClick={openAdjust}>
            {t('stock.adjust')}
          </Button>
          <Button variant="contained" startIcon={<SwapHorizIcon />} onClick={openTransfer}>
            {t('stock.transfer')}
          </Button>
        </Stack>
      </Stack>

      <TextField
        select
        label={t('stock.location')}
        value={effectiveKey}
        onChange={(event) => {
          setLocationKey(event.target.value);
          setMovementPage(1);
        }}
        sx={{ maxWidth: 420 }}
      >
        {locationOptions.map((option) => (
          <MenuItem key={option.key} value={option.key}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h2">{t('stock.onHand')}</Typography>
            {balance.isFetching && <CircularProgress size={18} />}
          </Stack>

          {balance.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t(errorKey(balance.error))}
            </Alert>
          )}

          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('fields.sku')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('fields.product')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('fields.unit')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {t('fields.quantity')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balance.isPending && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box sx={{ py: 3, display: 'grid', placeItems: 'center' }}>
                        <CircularProgress size={22} />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!balance.isPending && (balance.data?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                        {t('stock.empty')}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {(balance.data ?? []).map((row: StockBalance) => (
                  <TableRow key={row.productId} hover>
                    <TableCell>{row.productSku}</TableCell>
                    <TableCell>{row.productName}</TableCell>
                    <TableCell>{t(`units.${row.unit}`)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {Number.parseFloat(row.quantity).toLocaleString(
                        locale === 'it' ? 'it-IT' : 'tr-TR',
                        { maximumFractionDigits: 3 },
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {(balance.data?.length ?? 0) > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {t('stock.summary', {
                  products: balance.data?.length ?? 0,
                  units: totalUnits.toLocaleString(locale === 'it' ? 'it-IT' : 'tr-TR', {
                    maximumFractionDigits: 3,
                  }),
                })}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            {t('stock.movements')}
          </Typography>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t('fields.date')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('fields.type')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('fields.product')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t('fields.quantity')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('fields.reason')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(movements.data?.items ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                          {t('common.noData')}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {(movements.data?.items ?? []).map((movement) => {
                    const quantity = Number.parseFloat(movement.quantity);
                    return (
                      <TableRow key={movement.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(movement.occurredAt, locale)}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={t(`stock.types.${movement.type}`)} />
                        </TableCell>
                        <TableCell>{movement.productName}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            // The sign is what matters most in a stock log.
                            color: quantity < 0 ? 'error.main' : 'success.main',
                            fontWeight: 500,
                          }}
                        >
                          {quantity > 0 ? '+' : ''}
                          {quantity.toLocaleString(locale === 'it' ? 'it-IT' : 'tr-TR', {
                            maximumFractionDigits: 3,
                          })}
                        </TableCell>
                        <TableCell>{movement.reason ?? '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={movements.data?.total ?? 0}
              page={Math.max(0, movementPage - 1)}
              onPageChange={(_, next) => setMovementPage(next + 1)}
              rowsPerPage={movementPageSize}
              onRowsPerPageChange={(event) => {
                setMovementPageSize(Number(event.target.value));
                setMovementPage(1);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={t('common.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('common.displayedRows', { from, to, count })
              }
            />
          </Paper>
        </CardContent>
      </Card>

      <FormDialog
        open={transferOpen}
        title={t('stock.transferTitle')}
        onClose={() => setTransferOpen(false)}
        onSubmit={() => void submitTransfer()}
        submitting={transferSaving}
        errorKey={transferError.errorKey}
        maxWidth="md"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              select
              label={t('stock.from')}
              value={fromKey}
              onChange={(event) => setFromKey(event.target.value)}
              fullWidth
            >
              {locationOptions.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <SwapHorizIcon color="action" />
            <TextField
              select
              label={t('stock.to')}
              value={toKey}
              onChange={(event) => setToKey(event.target.value)}
              fullWidth
            >
              {locationOptions
                // Source and destination must differ; the API refuses it anyway.
                .filter((option) => option.key !== fromKey)
                .map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
            </TextField>
          </Stack>

          <Divider />

          {lines.map((line, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                select
                label={t('fields.product')}
                value={line.productId}
                onChange={(event) =>
                  setLines((current) =>
                    current.map((item, i) =>
                      i === index ? { ...item, productId: event.target.value } : item,
                    ),
                  )
                }
                fullWidth
              >
                {(products.data?.items ?? []).map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.sku} — {product.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('fields.quantity')}
                value={line.quantity}
                onChange={(event) =>
                  setLines((current) =>
                    current.map((item, i) =>
                      i === index ? { ...item, quantity: event.target.value } : item,
                    ),
                  )
                }
                inputMode="decimal"
                sx={{ width: 140 }}
              />
              <IconButton
                size="small"
                color="error"
                disabled={lines.length === 1}
                onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                aria-label={t('common.delete')}
              >
                ×
              </IconButton>
            </Stack>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={() => setLines((current) => [...current, { productId: '', quantity: '' }])}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('stock.addLine')}
          </Button>
        </Stack>
      </FormDialog>

      <FormDialog
        open={adjustOpen}
        title={t('stock.adjustTitle')}
        onClose={() => setAdjustOpen(false)}
        onSubmit={() => void submitAdjust()}
        submitting={adjustSaving}
        errorKey={adjustError.errorKey}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            {t('stock.adjustOn', {
              location: locationOptions.find((o) => o.key === effectiveKey)?.label ?? '',
            })}
          </Alert>

          <TextField
            select
            label={t('fields.type')}
            value={adjustType}
            onChange={(event) =>
              setAdjustType(event.target.value as typeof adjustType)
            }
            fullWidth
          >
            <MenuItem value="purchase_in">{t('stock.types.purchase_in')}</MenuItem>
            <MenuItem value="adjustment">{t('stock.types.adjustment')}</MenuItem>
            <MenuItem value="write_off">{t('stock.types.write_off')}</MenuItem>
          </TextField>

          <TextField
            select
            label={t('fields.product')}
            value={adjustProductId}
            onChange={(event) => setAdjustProductId(event.target.value)}
            fullWidth
          >
            {(products.data?.items ?? []).map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.sku} — {product.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t('fields.quantity')}
            value={adjustQuantity}
            onChange={(event) => setAdjustQuantity(event.target.value)}
            helperText={
              adjustType === 'write_off'
                ? t('stock.writeOffHelp')
                : adjustType === 'adjustment'
                  ? t('stock.adjustmentHelp')
                  : undefined
            }
            inputMode="decimal"
            fullWidth
          />

          <TextField
            label={t('fields.reason')}
            value={adjustReason}
            onChange={(event) => setAdjustReason(event.target.value)}
            required
            fullWidth
          />
        </Stack>
      </FormDialog>
    </Stack>
  );
}

/** Keeps the movement-type union in step with the shared constant. */
export type MovementType = (typeof STOCK_MOVEMENT_TYPES)[number];
